import { useAuth } from "@/features/auth/hooks/use-auth-react";
import { mapNpToNpDto } from "@/features/user/types";
import { isIdentityValid } from "@/utils/declarative.utils";
import { useEffect, useRef, useState } from "react";
import { WelcomeStep } from "./components/welcome-step.react";
import { DocumentTypeStep } from "./components/document-type-step.react";
import { KYCDocumentType } from "@/features/kyc/types";
import Loader from "@/atoms-react/loader/Loader.react";
import { IdentificationSuccessful } from "./components/identification-successful.react";
import { IdentificationFailed } from "./components/identification-failed.react";
import { IdentificationPending } from "./components/identification-pending.react";
import { AlgoregSessionState, AlgoregSessionStatusType } from "@/features/algoreg-session/types";
import { usei18n } from "@/features/utils/hooks/use-i18n";
import { useAlgoregSession } from "@/features/algoreg-session/use-algoreg-session";
import { KYCIdentitySessionIFrame } from "./components/kyc-identity-session-iframe";
import { useUser } from "@/features/user/hooks/use-user";
import { useKYC } from "@/features/kyc/hooks/use-kyc";
import { asyncRetry } from "@/utils/technical.utils";
import toast from "react-hot-toast";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { useTranslation } from "react-i18next";

enum KYCIdentityStep {
  Welcome = "Welcome",
  SelectDocumentType = "SelectDocumentType",
  IdentitySession = "IdentitySession",
  Loading = "Loading",
  Success = "Success",
  Fail = "Fail",
  Pending = "Pending"
}

type KYCIdentityProps = {
  onValidated?: () => void;
};

export const KYCIdentity = ({ onValidated }: KYCIdentityProps) => {
  const translation = useTranslation(["error"]);
  const { getLanguage } = usei18n();
  const { user } = useAuth();
  const { useUserProfile } = useUser();
  const { initAlgoregSessionMutation, finishAlgoregSessionMutation } = useAlgoregSession();
  const { KYCStateQuery } = useKYC();
  const { mutateAsync: initAlgoregSession } = initAlgoregSessionMutation();
  const { mutateAsync: finishAlgoregSession } = finishAlgoregSessionMutation();
  const { data: useProfile, refetch: refetchUserProfile } = useUserProfile();
  const { refetch: refetchKycSessionStatus } = KYCStateQuery(
    useProfile?.naturalPerson.UUID ?? null
  );
  const [algoregSession, setAlgoregSession] = useState<AlgoregSessionState | null>(null);
  const [step, setStep] = useState<KYCIdentityStep>(KYCIdentityStep.Welcome);
  const finishIsProcessing = useRef<boolean>(false);

  const isNextDisabled =
    user.data?.naturalPerson === undefined
      ? true
      : !isIdentityValid(mapNpToNpDto(user.data?.naturalPerson));

  useEffect(() => {
    return () => {
      // We need to refresh the profile at the end, but when we do that, it closes the modal too soon.
      // So we need to do it only when the component is destroyed.
      refetchUserProfile();
    };
  }, []);

  useEffect(() => {
    const olkyPassLevel = useProfile?.olkyPassLevel.olkypass_status.level ?? 0;
    if (olkyPassLevel >= 2) {
      setStep(KYCIdentityStep.Success);
    }
  }, [useProfile]);

  const initSession = async (documentTypeCode: number) => {
    setStep(KYCIdentityStep.Loading);

    try {
      const response = await asyncRetry(() =>
        initAlgoregSession({
          language: getLanguage(),
          documentTypeCode
        })
      );

      setAlgoregSession(response);
      setStep(KYCIdentityStep.IdentitySession);
    } catch (e) {
      toast.error(extractErrorDescription(e, translation));
      setStep(KYCIdentityStep.Welcome);
    }
  };

  const finishSession = async (iframeSuccess: boolean) => {
    if (finishIsProcessing.current) return;
    finishIsProcessing.current = true;

    if (!iframeSuccess) {
      setStep(KYCIdentityStep.Fail);
      return;
    }

    setStep(KYCIdentityStep.Loading);

    try {
      const { statusType } = await asyncRetry(() =>
        finishAlgoregSession({
          sessionId: algoregSession?.sessionId ?? ""
        })
      );

      if (statusType === AlgoregSessionStatusType.NEGATIVE) {
        setStep(KYCIdentityStep.Fail);
        return;
      }

      const { data: olkyPass } = await refetchKycSessionStatus();

      if (!olkyPass) {
        setStep(KYCIdentityStep.Fail);
        return;
      }

      const olkyPassLevel = olkyPass.olkypass_status.level;
      const olkyPassUnderAuditLevel = olkyPass.olkypass_status_under_audit.level;
      const auditPending =
        olkyPassUnderAuditLevel && olkyPassLevel === 1 && olkyPassUnderAuditLevel > olkyPassLevel;

      if ((olkyPassLevel as number) > 1) {
        setStep(KYCIdentityStep.Success);
      } else if (auditPending) {
        setStep(KYCIdentityStep.Pending);
      } else {
        setStep(KYCIdentityStep.Fail);
      }
    } catch (_) {
      setStep(KYCIdentityStep.Fail);
    }
  };

  return (
    <div className={"flex h-full min-h-[30rem] opacity-1"}>
      <div className={"m-3 flex w-full flex-1 flex-col justify-end justify-center gap-10 sm:m-0"}>
        {(() => {
          switch (step) {
            case KYCIdentityStep.Welcome:
              return (
                <WelcomeStep
                  isNextDisabled={isNextDisabled}
                  onValidated={() => setStep(KYCIdentityStep.SelectDocumentType)}
                />
              );
            case KYCIdentityStep.SelectDocumentType:
              return (
                <DocumentTypeStep
                  onPrevious={() => setStep(KYCIdentityStep.Welcome)}
                  custom_scenario={false}
                  onValidated={(documentType: KYCDocumentType) => {
                    initSession(documentType?.code);
                  }}
                />
              );
            case KYCIdentityStep.IdentitySession:
              if (!algoregSession) return <LoaderStep />;

              return (
                <KYCIdentitySessionIFrame
                  className={"h-[89vh]"}
                  algoregSession={algoregSession}
                  params={{
                    powered_by: false,
                    show_consent: false,
                    show_home: true,
                    show_final: false
                  }}
                  onFinish={finishSession}
                />
              );
            case KYCIdentityStep.Loading:
              return <LoaderStep />;
            case KYCIdentityStep.Success:
              return <IdentificationSuccessful onValidated={onValidated} />;
            case KYCIdentityStep.Fail:
              return <IdentificationFailed onValidated={onValidated} />;
            case KYCIdentityStep.Pending:
              return <IdentificationPending onValidated={onValidated} />;
          }
        })()}
      </div>
    </div>
  );
};

const LoaderStep = () => (
  <div className="flex flex-1 items-center justify-center">
    <Loader className="text-gray-500" />
  </div>
);
